goog.provide("el8.uiwidget.NewDialog");

goog.require("el8.uiwidget.ButtonSet");
goog.require("el8.template.uiwidget.NewDialog");
goog.require("el8.util.base");
goog.require("el8.util.consts");

/**
 * This is the base class for current dialogs (i.e., there are some
 * older dialogs using deprecated base classes, but this is what you'll
 * use for anything new and what most existing code uses as well).
 *
 * It's used directly for dialogs that are non-interactive (e.g., viewInlinePdf.js)
 * or have a non-form-based interaction scheme (e.g., selectAttachments.js).
 * It is also the parent class for FormDialog, which is used in many places.
 */
el8.uiwidget.NewDialog = function (spec) {
    spec = spec || {};

    this._isInit = false;

    /**
     * True if the dialog auto adjusts its height
     *
     * NOTE: autoHeight messes a bit with jquery UI Dialog's
     * "center" positioning. If need to guarantee "center",
     * use explicit height.
     */
    this._autoHeight = spec.autoHeight === true; // defaults to false
    this._autoPosition = spec.autoPosition === true; // defaults to false
    this._position = spec.position || this._getPositionObject("center");

    this._contentFn = spec.contentFn || null;
    this._minimizeFn = spec.minimizeFn || null;
    this._closeFn = spec.closeFn || null;
    this._showXButton = spec.showXButton === true; // defaults to false
    this._closeOnEscape = this._showXButton
        ? // default to true if showXButton is true; default to false otherwise
          spec.closeOnEscape !== false
        : spec.closeOnEscape === true;

    this._testId = spec.testId || null;

    /**
     * Dialog's title.
     * @type {string}
     * @private
     */
    this._title = spec.title || "";
    /**
     * True if the dialog should have modal behavior i.e. other
     * items on the page cannot be interacted with
     */
    this._modal = spec.modal === true; // defaults to false
    this._draggable = spec.draggable !== false; // defaults to true
    /**
     * The buttonSet associated with this dialog.
     * @type {el8.uiwidget.ButtonSet}
     */
    this._buttonSet = spec.buttonSet || null;

    /**
     * The jqueryUI dialog.
     * @type {Object}
     */
    this._dialog = null;
    /**
     * Dialog's root element.
     * @type {Element}
     * @private
     */
    this._container = null;
    /**
     * Element for the content.
     * @type {Element}
     * @private
     */
    this._contentElt = null;
    /**
     * Element for the warning
     * @type {Element}
     * @private
     */
    this._warningElt = null;
    /**
     * Element for the buttonSet
     * @type {Element}
     * @private
     */
    this._buttonElt = null;

    /**
     *  The width of the dialog in pixels
     *  @type {number} or {function}
     */
    this._width = spec.width || 650;

    /**
     * The dialog height in pixels
     * @type {number} or {function}
     */
    this._height = spec.height || 400;

    /**
     * jQuery UI Draggable `scroll` option (w/ default off override to avoid page auto-scroll)
     * Enables container auto-scroll while dragging, typically, not useful for dialogs.
     * 
     * NOTE: When disabling auto-scroll, all parents in the dialog's hierarchy will also require
     * `overflow: hidden` and explicit height. e.g. html, body { overflow: hidden; height: 100%; }
     * @type {boolean}
     */
    this._scroll = (spec.scroll !== undefined) ? spec.scroll : false;

    /**
     * This decides the autoOpen flag passed the to the jquery dialog.
     * It should really be defaulted false, but our accidental historic
     * behavior has been to default it true, and at least some dialogs (MedsHx)
     * break when it is set to false, so it's risky to change the global.
     */
    this._openOnInit = spec.openOnInit !== false; // defaults to true
};;;;

/*************************
 * Basic methods to set
 * rendering parameters
 *************************/

/**
 * Allows arbitrary HTML to be set in the content element.
 * @param {string} content Content html.
 */
el8.uiwidget.NewDialog.prototype.setContent = function (html) {
    if (this._contentElt) {
        this._contentElt.html(html); // XSS OK -KF (need to rely on caller not to do anything stupid
    }
};
/**
 * @returns {Element} The element wrapping the content.
 */
el8.uiwidget.NewDialog.prototype.getContentElt = function () {
    return this._contentElt;
};

/**
 * @returns {Element} The element wrapping the warning
 */
el8.uiwidget.NewDialog.prototype.getWarningElt = function () {
    return this._warningElt;
};

el8.uiwidget.NewDialog.prototype.getButtonElt = function () {
    return this._buttonElt;
};

/**
 * Sets whether the dialog should be modal or not.
 */
el8.uiwidget.NewDialog.prototype.setModal = function (modal) {
    this._modal = modal;
};

/**
 * Sets title of dialog; requires dialog to already be initialized
 */
el8.uiwidget.NewDialog.prototype.setTitle = function (title) {
    if (title || title === "") {
        this._container.dialog("option", "title", title);
    }
};

/*************************
 * Basic methods to get
 * rendering parameters
 *************************/
/**
 * @returns {Element} The root element for this dialog.
 */
el8.uiwidget.NewDialog.prototype.getContainer = function () {
    return this._container;
};

/**
 * @returns {Element} The dialog div itself; only available after dialog is constructed
 */
el8.uiwidget.NewDialog.prototype.getDialogContainer = function () {
    if (!this._container) {
        return null;
    }
    return this._container.closest(".ui-dialog");
};

el8.uiwidget.NewDialog.prototype.getTitleElt = function () {
    var dialogContainer = this.getDialogContainer();
    if (dialogContainer) {
        return $(".ui-dialog-titlebar", dialogContainer);
    }
    return null;
};

el8.uiwidget.NewDialog.prototype.getDialogContentElt = function () {
    var dialogContainer = this.getDialogContainer();
    if (dialogContainer) {
        return $(".ui-dialog-content", dialogContainer);
    }
    return null;
};

/*****************************
 * Main methods for displaying
 * the dialog
 *****************************/
/**
 * Creates the DOM representation for the dialog.

 * By default this will also show the dialog in a half-functional state
 * (because the jQuery UI dialog will auto-open but our show function won't have run).
 * _init is almost always invoked implicitly by show, but if you want to explicitly
 * invoke it (to add stuff to the DOM without showing the dialog, e.g.),
 * be sure to set openOnInit to false.
 */
el8.uiwidget.NewDialog.prototype._init = function () {
    if (this._isInit) {
        return;
    }

    this._container = $(
        el8.template.uiwidget.NewDialog.container({
            testId: this._testId,
        })
    );

    $(document.body).append(this._container);
    this._contentElt = this._container.find(".dialog-content");

    if (this._contentFn) {
        this.setContent(this._contentFn());
    }

    var that = this;
    var opts = {
        disableTitlebarMinimize: !this._minimizeFn,
        disableTitlebarClose: !this._closeFn && !this._showXButton,
        minimizeFn: this._minimizeFn,
        disableTitlebarExpand: true,
        closeOnEscape: false,
        width: typeof this._width === "function" ? this._width() : this._width,
        height:
            typeof this._height === "function" ? this._height() : this._height,
        autoOpen: this._openOnInit,
        zIndex: 1000,
    };
    opts.title = this._title;
    opts.modal = this._modal;
    opts.resizable = false;
    opts.draggable = this._draggable;
    opts.position = this._getPositionObject(this._position);
    opts.closeFn =
        this._closeFn ||
        function () {
            that.hide();
        };
    opts.scroll = this._scroll;

    this._dialog = this._container.dialog(opts);

    /**
     * Add class to dialogContainer
     */
    this.getDialogContainer().addClass("el8Dialog");

    // Attach buttons after dialog is rendered, so jqueryui does not destroy them
    this._warningElt = $(el8.template.uiwidget.NewDialog.warningContainer());
    this._container.append(this._warningElt);
    this._buttonElt = $(el8.template.uiwidget.NewDialog.buttonsContainer());
    this._container.append(this._buttonElt);

    if (this._testId) {
        $(".ui-dialog-titlebar-close", this._container.parent()).attr(
            "data-testid",
            this._testId + "-x-button"
        );
        $(".ui-dialog-titlebar-min", this._container.parent()).attr(
            "data-testid",
            this._testId + "-min-button"
        );
    }

    if (this._buttonSet) {
        this._buttonSet.setContainer(this._buttonElt).render();
    }

    // Close dialog upon hitting ESC
    if (this._closeOnEscape) {
        this.getDialogContainer().on("keydown", function (e) {
            if (e.keyCode === KEYS.ESC) {
                e.preventDefault();
                that.hide();
            }
        });
    }

    this._isInit = true;
};

/**
 * Displays the dialog on the page, rendering it if necessary.
 */
el8.uiwidget.NewDialog.prototype.show = function () {
    if (!this._isInit) {
        this._init();
    }

    this._container.dialog("open");

    if (this._buttonSet) {
        this._buttonSet.bindEvents();
    }

    if (this._modal) {
        $(".ui-widget-overlay").addClass("el8ModalDialogOverlay");
    }

    this.adjustHeight();

    var that = this;
    $(window).on(
        "resize",
        (this._adjustPositionFn = _.debounce(function () {
            that.adjustPosition();
        }, el8.util.consts.RESIZE_RATE))
    );
};

el8.uiwidget.NewDialog.prototype.isOpen = function () {
    return this._container.dialog("isOpen");
};

/**
 * Hides the dialog
 */
el8.uiwidget.NewDialog.prototype.hide = function () {
    // TODO We should offer a "cancel" method in addition to
    // a hide method. This way, the "X" can be hooked up to the
    // cancel method, which can then be overridden, rather than
    // the overriding the hide method, which has many other uses
    if (!this._isInit) {
        return;
    }
    if (this._buttonSet) {
        this._buttonSet.unbindEvents();
    }
    this._container.dialog("close");

    if (this._adjustPositionFn) {
        $(window).off("resize", this._adjustPositionFn);
    }
};

el8.uiwidget.NewDialog.prototype.destroy = function () {
    if (this._container) {
        this._container.dialog("destroy");
        this._container.remove();
    }
};

el8.uiwidget.NewDialog.prototype.bringForward = function () {
    if (this._container) {
        this._container.dialog("moveToTop");
    }
};

/**
 * Auto-adjusts height of dialog
 */
el8.uiwidget.NewDialog.prototype._getInnerContentHeight = function () {
    return this.getContentElt().contents().outerHeight(true);
};

el8.uiwidget.NewDialog.prototype._adjustContentHeight = function () {
    /* This does not enforce minimum/maximum heights; is meant to be a response
     * to the container hitting a minimum or maximum constraint
     */
    var buttonHeight = $(".ui-dialog-buttonpane", this._container).outerHeight(
        true
    );
    var warningHeight = $(".ui-dialog-warning", this._container).outerHeight(
        true
    );

    var content = this.getContentElt();
    var dialogHeight = this._container.outerHeight(true);
    var padding = content.outerHeight(true) - content.height();
    content.height(dialogHeight - warningHeight - buttonHeight - padding);
    this.getDialogContainer().css("height", "auto");
};

el8.uiwidget.NewDialog.prototype._adjustContentTop = function () {
    var winHeight = window.innerHeight;
    var dialogTop = parseInt(
        this._contentElt.first().offsetParent()[0].style.top
    );
    var dialogHeight = this._container.parent().height();
    // Checking the autoPosition setting again here below because we were
    // Seeing this method being called mysteriously during dialog init and
    // could not tell how from the traceback.
    if (this._autoPosition && dialogTop + dialogHeight > winHeight) {
        var newTop = Math.max(0, winHeight - dialogHeight);
        this.getDialogContainer().css("top", newTop);
    }
};

el8.uiwidget.NewDialog.prototype.adjustHeight = function () {
    if (this._autoHeight) {
        /* If autoHeight, then adjust size of dialog to fit content */
        var buttonHeight = $(
            ".ui-dialog-buttonpane",
            this._container
        ).outerHeight(true);
        var warningHeight = $(
            ".ui-dialog-warning",
            this._container
        ).outerHeight(true);
        var innerContentHeight = this._getInnerContentHeight();
        var targetHeight = innerContentHeight + buttonHeight + warningHeight;
        if (targetHeight < this.getMinAutoHeight()) {
            this._container.height(this.getMinAutoHeight());
        } else if (targetHeight > this.getMaxAutoHeight()) {
            this._container.height(this.getMaxAutoHeight());
        } else {
            this._container.height(targetHeight);
        }
    }
    this._adjustContentHeight();

    if (this._autoPosition) {
        this._adjustContentTop();
    }
};

/**
 * Attaches the buttonSet to the correct spot in the dialog.
 * @param {el8.uiwidget.NewDialog.ButtonSet} buttons
 */
el8.uiwidget.NewDialog.prototype.setButtonSet = function (buttonSet) {
    this._buttonSet = buttonSet;
};

el8.uiwidget.NewDialog.prototype.displayLoading = function (msg) {
    this.getContentElt().hide();
    this._loadingElt = $(
        el8.template.uiwidget.NewDialog.loadingElt({ msg: msg || "" })
    );
    this.getContentElt().after(this._loadingElt);
    this._loadingElt.height(this.getContentElt().outerHeight(true));
};

el8.uiwidget.NewDialog.prototype.hideLoading = function () {
    if (this._loadingElt) {
        this._loadingElt.remove();
    }
    this.getContentElt().show();
};

el8.uiwidget.NewDialog.prototype.isOpen = function () {
    if (!this._isInit) {
        return false;
    }
    return this._container.dialog("isOpen");
};

el8.uiwidget.NewDialog.prototype.hideButtons = function () {
    if (this._buttonElt) {
        this._buttonElt.hide();
    }
};

el8.uiwidget.NewDialog.prototype.showButtons = function () {
    if (this._buttonElt) {
        this._buttonElt.show();
    }
};

// Utility function for converting jQuery UI 1.8 position
// strings into modern jQuery UI position objects.
//
// Accepts strings: "center", "left", "right", "top", "bottom"
//
// Defaults to center
el8.uiwidget.NewDialog.prototype._getPositionObject = function (pos) {
    switch (pos) {
        case "left":
            return { my: "left", at: "left", of: window };
        case "right":
            return { my: "right", at: "right", of: window };
        case "top":
            return { my: "top", at: "top", of: window };
        case "bottom":
            return { my: "bottom", at: "bottom", of: window };
        case "center":
        default:
            return { my: "center", at: "center", of: window };
    }
};

/**
 * Minimum and maximum container heights, if autoHeight is true
 * (currently arbitrary and static)
 */

el8.uiwidget.NewDialog.prototype.adjustPosition = function () {
    if (this._autoHeight) {
        this.adjustHeight();
    } else if (typeof this._height === "function") {
        this._container.dialog("option", "height", this._height());
        this._adjustContentHeight();
    }

    if (typeof this._width === "function") {
        this._container.dialog("option", "width", this._width());
    }

    if (this._position) {
        this._container.dialog(
            "option",
            "position",
            this._getPositionObject(this._position)
        );
    }
};

/**
 * Minimum and maximum container heights, if autoHeight is true
 * (currently arbitrary and static)
 */
el8.uiwidget.NewDialog.prototype.getMinAutoHeight = function () {
    return 150;
};

el8.uiwidget.NewDialog.prototype.getMaxAutoHeight = function () {
    return $(window).height() - 100;
};

/**
 * Set width
 */
el8.uiwidget.NewDialog.prototype.setWidth = function (width) {
    this._width = width;
    this._container.dialog(
        "option",
        "width",
        typeof this._width === "function" ? this._width() : this._width
    );
};

el8.uiwidget.NewDialog.prototype.getWidth = function () {
    return this._container.dialog("option", "width");
};
